import defaultRules from "../rules.tuning.json";
import type { BankCategory } from "../bank-types.js";
import type {
  OfferOption,
  ActiveCategory,
  BoardSlot,
  CategoryView,
  GuessResult,
  Phase,
  Player,
  RoomView,
  Team,
  TeamId,
} from "../game-types.js";
import { TEAM_IDS } from "../game-types.js";
import { themeOf } from "../themes.js";
import { isCountryList } from "../answer-kind.js";
import { isTooStale } from "../source-age.js";
import {
  DEFAULT_MATCHER_CONFIG,
  matchGuess,
  type MatchOutcome,
  type MatcherConfig,
  type MatchTarget,
} from "../matcher/matcher.js";

export type RulesConfig = typeof defaultRules;
export const DEFAULT_RULES: RulesConfig = defaultRules;

export type CloseReason = "cleared" | "strikes";

export interface EngineOptions {
  code: string;
  /**
   * Everything the match may draw from. Nothing is chosen at construction:
   * each round puts up its own board of choices and the teams ban from it.
   */
  categories: BankCategory[];
  rules?: RulesConfig;
  matcher?: MatcherConfig;
  /**
   * Picks `count` items from a pool. Used for both the ban board and the
   * round's draw. Injected so the engine stays free of shuffling and tests
   * stay deterministic.
   */
  draw?: <T>(pool: T[], count: number) => T[];
  /** Themes the host has struck out for good, before anyone joined. */
  excludedThemes?: string[];
  /**
   * Categories played in recent games on this server, oldest first, so a table
   * playing a second match does not meet last match's questions.
   *
   * Passed whole rather than pre-trimmed: only the engine knows how much of
   * the bank its own filters can actually reach, and that is the number this
   * memory has to be affordable against.
   */
  recentCategoryIds?: string[];
  /** Injected so tests are deterministic; the server passes Date.now. */
  now: number;
}

/** Fallback draw: take them in order. The server injects a real shuffle. */
const takeInOrder = <T>(pool: T[], count: number): T[] => pool.slice(0, count);

/** Long enough for a joke, short enough to fit the TV scoreboard. */
export const MAX_TEAM_NAME = 14;

const TEAM_DEFAULT_NAMES: Record<TeamId, string> = {
  rose: "Rose",
  slate: "Slate",
};

/**
 * The whole rule set, as a pure state machine.
 *
 * Nothing in here reads a clock or sets a timer — every entry point takes `now`.
 * The server owns the real clock and calls `tick`. That keeps a full match
 * simulatable in a test in microseconds.
 */
export class Engine {
  readonly code: string;
  phase: Phase = "lobby";
  teams: Record<TeamId, Team>;
  players: Player[] = [];
  /** Categories dealt so far, two more per round as each round's bans resolve. */
  categories: BankCategory[] = [];
  /** Everything the draw may choose from. */
  pool: BankCategory[];
  /** Which round is on, 1-based once the match starts. */
  round = 0;
  /** The current ban board, in display order. */
  offered: BankCategory[] = [];
  /** Every category id that has appeared on any board this game. */
  private offeredEver = new Set<string>();
  /** Ids actually dealt into play, for the server's cross-game memory. */
  private dealtIds: string[] = [];
  bans: Record<TeamId, string[]> = { rose: [], slate: [] };
  categoryIndex = -1;
  active: ActiveCategory | null = null;
  activeTeam: TeamId = "rose";
  activePlayerId: string | null = null;
  strikes = 0;
  deadline: number | null = null;
  lastResult: GuessResult | null = null;
  winner: TeamId | "tie" | null = null;

  /**
   * When this room opened. Used only to date the bank when filtering the draw —
   * a room lives for hours, so the year never moves under it, and the engine
   * still never reads a clock of its own.
   */
  private readonly createdAt: number;
  private rules: RulesConfig;
  private matcherCfg: MatcherConfig;
  private draw: <T>(pool: T[], count: number) => T[];
  private readonly excludedThemes: readonly string[];
  /** Oldest first — the tail is the most recently played. */
  private readonly recentCategoryIds: readonly string[];
  private seq = 0;
  /** Board entry the last rejected guess came closest to, for the TV override. */
  private lastClosestRank: number | null = null;

  constructor(opts: EngineOptions) {
    this.code = opts.code;
    this.createdAt = opts.now;
    this.pool = opts.categories;
    this.draw = opts.draw ?? takeInOrder;
    this.excludedThemes = opts.excludedThemes ?? [];
    this.recentCategoryIds = opts.recentCategoryIds ?? [];
    this.rules = opts.rules ?? DEFAULT_RULES;
    this.matcherCfg = opts.matcher ?? DEFAULT_MATCHER_CONFIG;
    this.teams = {
      rose: {
        id: "rose",
        name: TEAM_DEFAULT_NAMES.rose,
        score: 0,
        rotationIndex: -1,
      },
      slate: {
        id: "slate",
        name: TEAM_DEFAULT_NAMES.slate,
        score: 0,
        rotationIndex: -1,
      },
    };
  }

  /** Swap in freshly tuned config without disturbing a match in progress. */
  applyConfig(rules: RulesConfig, matcher: MatcherConfig): void {
    this.rules = rules;
    this.matcherCfg = matcher;
  }

  // ---------------------------------------------------------------- players

  addPlayer(id: string, name: string): Player {
    const existing = this.players.find((p) => p.id === id);
    if (existing) {
      existing.connected = true;
      existing.name = name;
      return existing;
    }
    // Join the smaller team so an unbalanced room self-corrects.
    const counts = this.teamCounts();
    const teamId: TeamId = counts.rose <= counts.slate ? "rose" : "slate";
    const player: Player = {
      id,
      name,
      teamId,
      connected: true,
      ready: false,
      correctCount: 0,
    };
    this.players.push(player);
    return player;
  }

  setTeam(playerId: string, teamId: TeamId): boolean {
    const player = this.players.find((p) => p.id === playerId);
    // Switching teams mid-turn would let a player dodge their own clock.
    if (!player || player.id === this.activePlayerId) return false;
    player.teamId = teamId;
    return true;
  }

  /**
   * Rename your own side, in the lobby only.
   *
   * Once the match is under way the name is on the scoreboard, in the callouts
   * and in everyone's memory of who is winning, so it stops being editable. A
   * blank name falls back to the default rather than leaving a nameless team.
   */
  setTeamName(playerId: string, name: string): string | null {
    if (this.phase !== "lobby") return "Names are locked once the match starts";
    const player = this.players.find((p) => p.id === playerId);
    if (!player) return "Unknown player";
    const trimmed = name.trim().slice(0, MAX_TEAM_NAME);
    this.teams[player.teamId].name =
      trimmed || TEAM_DEFAULT_NAMES[player.teamId];
    return null;
  }

  setReady(playerId: string, ready: boolean): boolean {
    if (this.phase !== "lobby") return false;
    const player = this.players.find((p) => p.id === playerId);
    if (!player) return false;
    player.ready = ready;
    return true;
  }

  /**
   * Disconnected players are not counted: someone whose phone died must not be
   * able to hold the whole room hostage.
   */
  allReady(): boolean {
    const present = this.players.filter((p) => p.connected);
    return present.length > 0 && present.every((p) => p.ready);
  }

  /** How close the lobby is to going, for the TV to display. */
  readyCount(): { ready: number; total: number } {
    const present = this.players.filter((p) => p.connected);
    return {
      ready: present.filter((p) => p.ready).length,
      total: present.length,
    };
  }

  /**
   * Begin as soon as the room has said it is ready. Called by the server after
   * anything that could change the answer — a ready toggle, a team switch, a
   * join, or a disconnect.
   */
  maybeAutoStart(now: number): boolean {
    if (this.phase !== "lobby") return false;
    if (this.canStart() !== null || !this.allReady()) return false;
    return this.start(now);
  }

  setConnected(playerId: string, connected: boolean, now: number): void {
    const player = this.players.find((p) => p.id === playerId);
    if (!player) return;
    player.connected = connected;
    // Losing signal on your turn hands off without costing your team a strike.
    if (
      !connected &&
      this.phase === "playing" &&
      this.activePlayerId === playerId
    ) {
      this.activePlayerId = this.nextPlayerId(this.activeTeam);
      if (this.activePlayerId === null) this.closeCategory("strikes", now);
      else this.deadline = now + this.rules.answerSeconds * 1000;
    }
  }

  private teamCounts(): Record<TeamId, number> {
    return {
      rose: this.players.filter((p) => p.teamId === "rose").length,
      slate: this.players.filter((p) => p.teamId === "slate").length,
    };
  }

  /** Advance the given team's rotation and return whose turn it now is. */
  private nextPlayerId(teamId: TeamId): string | null {
    const roster = this.players.filter((p) => p.teamId === teamId);
    if (roster.length === 0) return null;
    const connected = roster.filter((p) => p.connected);
    const pool = connected.length > 0 ? connected : roster;
    const team = this.teams[teamId];
    team.rotationIndex = (team.rotationIndex + 1) % pool.length;
    return pool[team.rotationIndex]!.id;
  }

  canStart(): string | null {
    const counts = this.teamCounts();
    const min = this.rules.minPlayersPerTeam;
    if (counts.rose < min || counts.slate < min) {
      return `Each team needs at least ${min} player${min === 1 ? "" : "s"}`;
    }
    if (this.pool.length === 0) return "No categories loaded";
    return null;
  }

  // ---------------------------------------------------------- round boards

  /** How many categories the whole match intends to play. */
  private totalPlanned(): number {
    return this.rules.roundsPerMatch * this.rules.categoriesPerRound;
  }

  /** How many a whole match puts in front of the room, played or merely offered. */
  private totalOffered(): number {
    return this.rules.roundsPerMatch * this.rules.choicesPerRound;
  }

  /**
   * Other games' categories, trimmed to what this bank can afford to avoid.
   *
   * The memory is honoured newest-first and forgotten oldest-first, and never
   * grows past the point where a table would have less than two matches' worth
   * of unseen material left. Sized against the pool the draw can actually
   * reach — retirement puts most of the bank out of play, so measuring against
   * the whole bank would make this guard inert exactly when it matters.
   */
  private affordableRecent(playableCount: number): Set<string> {
    const budget = Math.max(0, playableCount - this.totalOffered() * 2);
    return new Set(
      this.recentCategoryIds.slice(
        Math.max(0, this.recentCategoryIds.length - budget),
      ),
    );
  }

  /**
   * The pool minus anything too obscure to be worth putting on a board.
   *
   * Categories are honestly tagged 1 (a room gets several) to 5 (deep cut), and
   * most of the bank sits at 4 and 5 — which made real games feel like a
   * specialist quiz. The cap is what keeps answers deducible rather than
   * merely true; raise it in the tuner for a harder crowd.
   */
  private playablePool(): BankCategory[] {
    // Retirement is a hard exclusion and sits outside the widening below: the
    // difficulty cap and the staleness rule are the draw judging a board too
    // hard or too old *today*, so relaxing them when the pool thins is right.
    // A retired board was taken out deliberately, and a thin pool is not a
    // reason to put it back in front of a room.
    const active = this.pool.filter((c) => !c.retired);
    const capped = active.filter(
      (c) =>
        c.difficulty <= this.rules.maxDifficulty &&
        // A continuously remeasured ranking whose source has aged out is worse
        // than a hard question: it is confidently wrong, and the room can tell.
        // Stable categories are never excluded however old their source is.
        !isTooStale(c, this.createdAt, this.rules.maxVolatileSourceAgeYears),
    );
    // Never let the filters empty the game out entirely.
    if (capped.length >= this.totalPlanned()) return capped;
    return active.length > 0 ? active : this.pool;
  }

  /**
   * What this round's board may be drawn from, in strict preference order:
   * fresh in every sense first, then each memory is forgotten in turn — other
   * games' categories before this game's boards — because a repeat from last
   * week is cheaper than a board that cannot fill. Only what has actually been
   * dealt into play this game is excluded without appeal.
   */
  private boardCandidates(): BankCategory[] {
    const playable = this.playablePool();
    const dealt = new Set(this.dealtIds);
    const need = this.rules.choicesPerRound;
    const recent = this.affordableRecent(playable.length);

    const tiers = [
      playable.filter(
        (c) =>
          !dealt.has(c.id) && !this.offeredEver.has(c.id) && !recent.has(c.id),
      ),
      playable.filter((c) => !dealt.has(c.id) && !this.offeredEver.has(c.id)),
      playable.filter((c) => !dealt.has(c.id)),
    ];
    for (const tier of tiers) if (tier.length >= need) return tier;
    return tiers[tiers.length - 1]!;
  }

  /**
   * Put up this round's board of choices.
   *
   * Diversity needs enforcing rather than hoping: a third of the playable bank
   * is "name some countries" in one guise or another, so an unguarded draw
   * regularly offers a board that feels like one long geography round. Quotas
   * are applied greedily over a shuffled list, and relaxed rather than allowed
   * to produce a short board.
   */
  private offerBoard(): BankCategory[] {
    let candidates = this.boardCandidates();

    // The host's standing theme bans, struck before anyone sat down. Applied
    // only while enough survives to keep the board a real choice.
    const excludedThemes = new Set(this.excludedThemes);
    const kept = candidates.filter((c) => !excludedThemes.has(themeOf(c)));
    if (kept.length >= this.rules.choicesPerRound) candidates = kept;

    const wanted = this.rules.choicesPerRound;
    const shuffled = this.draw(candidates, candidates.length);

    const picked: BankCategory[] = [];
    const perTheme = new Map<string, number>();
    let countryLists = 0;

    for (const category of shuffled) {
      if (picked.length >= wanted) break;
      const theme = themeOf(category);
      if ((perTheme.get(theme) ?? 0) >= this.rules.maxPerThemePerBoard)
        continue;
      const isCountry = isCountryList(category);
      if (isCountry && countryLists >= this.rules.maxCountryListsPerBoard)
        continue;

      picked.push(category);
      perTheme.set(theme, (perTheme.get(theme) ?? 0) + 1);
      if (isCountry) countryLists++;
    }

    // A thin pool can make the quotas unsatisfiable. Better a samey board than
    // a short one, so top up from whatever is left.
    if (picked.length < wanted) {
      const taken = new Set(picked.map((c) => c.id));
      for (const category of shuffled) {
        if (picked.length >= wanted) break;
        if (!taken.has(category.id)) picked.push(category);
      }
    }

    for (const c of picked) this.offeredEver.add(c.id);
    return picked;
  }

  banCategory(playerId: string, categoryId: string): string | null {
    if (this.phase !== "category-ban") return "Not banning right now";
    const player = this.players.find((p) => p.id === playerId);
    if (!player) return "Unknown player";
    if (!this.offered.some((c) => c.id === categoryId))
      return "That category is not on the board";
    if (this.bannedBy(categoryId) !== null) return "Already struck out";
    const mine = this.bans[player.teamId];
    if (mine.length >= this.rules.bansPerTeam)
      return "Your team has used its ban";
    mine.push(categoryId);
    return null;
  }

  /** The ban board as clients see it. Empty outside the ban phase. */
  private offerViews(): OfferOption[] {
    if (this.phase !== "category-ban") return [];
    return this.offered.map((c) => ({
      id: c.id,
      // The subject, not the question — see BankCategory.short. Falls back to
      // the prompt so a board without a label is long-winded, never blank.
      label: c.short ?? c.prompt,
      topic: c.topic,
      difficulty: c.difficulty,
      bannedBy: this.bannedBy(c.id),
    }));
  }

  bannedBy(categoryId: string): TeamId | null {
    if (this.bans.rose.includes(categoryId)) return "rose";
    if (this.bans.slate.includes(categoryId)) return "slate";
    return null;
  }

  private bansComplete(): boolean {
    return TEAM_IDS.every((t) => this.bans[t].length >= this.rules.bansPerTeam);
  }

  /**
   * Draw this round's categories from what survived the bans, then play on.
   *
   * If the bans somehow left too little standing, banned choices come back
   * rather than the round running short — a thin round is a worse outcome
   * than a ban being diluted.
   */
  private resolveBansAndDeal(now: number): void {
    const banned = new Set([...this.bans.rose, ...this.bans.slate]);
    let survivors = this.offered.filter((c) => !banned.has(c.id));
    if (survivors.length < this.rules.categoriesPerRound)
      survivors = this.offered;

    const dealt = this.draw(survivors, this.rules.categoriesPerRound)
      // Within the round, open on the gentler category.
      .sort((a, b) => a.difficulty - b.difficulty);

    for (const c of dealt) this.dealtIds.push(c.id);
    this.categories.push(...dealt);
    this.offered = [];
    // Point at the first of the freshly dealt categories and open it. Not
    // `nextCategory`: on a mid-match round the index was already advanced by
    // the boundary that got us here, and advancing it again would silently
    // skip the round's first category.
    this.categoryIndex = this.categories.length - dealt.length;
    this.openCategory(now);
  }

  /** What this game has actually put into play, for cross-game memory. */
  playedCategoryIds(): readonly string[] {
    return this.dealtIds;
  }

  // ------------------------------------------------------------------ flow

  start(now: number): boolean {
    if (this.phase !== "lobby" || this.canStart() !== null) return false;
    this.round = 0;
    this.beginRound(now);
    return true;
  }

  /**
   * Open the next round: put six choices up and let the teams ban, or — with
   * bans disabled — deal straight from the same board nobody sees. After the
   * last round there is nothing to open, so the match ends here.
   */
  private beginRound(now: number): void {
    this.round++;
    if (this.round > this.rules.roundsPerMatch) return this.endMatch();

    this.offered = this.offerBoard();
    this.bans = { rose: [], slate: [] };

    if (this.rules.bansEnabled && this.rules.bansPerTeam > 0) {
      this.phase = "category-ban";
      this.deadline = now + this.rules.banSeconds * 1000;
      return;
    }
    this.resolveBansAndDeal(now);
  }

  /**
   * Called after a ban lands.
   *
   * Both teams having banned does not begin the match — it shortens the clock.
   * Resolving on the spot meant the second strike appeared and the screen was
   * already gone, so nobody ever saw what the other team took off the table.
   * The board holds for `banRevealSeconds` with both bans struck, and the
   * ordinary `tick` resolves it, the same path a timed-out ban phase takes.
   */
  maybeFinishBans(now: number): boolean {
    if (this.phase !== "category-ban" || !this.bansComplete()) return false;
    const showUntil = now + this.rules.banRevealSeconds * 1000;
    // Never extends the phase: a ban landing with one second left on the clock
    // must not buy the room another two and a half.
    this.deadline =
      this.deadline === null ? showUntil : Math.min(this.deadline, showUntil);
    return false;
  }

  private nextCategory(now: number): void {
    this.categoryIndex++;
    // The dealt list runs out exactly at a round boundary: the next round
    // puts up its own board, and the last one ends the match instead.
    if (this.categoryIndex >= this.categories.length)
      return this.beginRound(now);
    this.openCategory(now);
  }

  /** Put the category at `categoryIndex` up on the board. */
  private openCategory(now: number): void {
    const bank = this.categories[this.categoryIndex]!;
    this.active = {
      bank,
      index: this.categoryIndex,
      slots: bank.answers.map((answer, i) => ({
        rank: i + 1,
        points: this.pointsForRank(i + 1),
        answer,
        revealed: false,
      })),
    };
    this.strikes = 0;
    this.lastResult = null;
    this.lastClosestRank = null;
    // Alternate who opens each category so neither team owns the first look.
    this.activeTeam = this.categoryIndex % 2 === 0 ? "rose" : "slate";
    this.activePlayerId = null;
    this.phase = "category-intro";
    this.deadline = now + this.rules.categoryIntroSeconds * 1000;
  }

  private beginPlay(now: number): void {
    this.phase = "playing";
    this.activePlayerId = this.nextPlayerId(this.activeTeam);
    if (this.activePlayerId === null) {
      // A team emptied out mid-match; give the turn to the other side.
      this.activeTeam = this.other(this.activeTeam);
      this.activePlayerId = this.nextPlayerId(this.activeTeam);
      if (this.activePlayerId === null)
        return this.closeCategory("strikes", now);
    }
    this.deadline = now + this.rules.answerSeconds * 1000;
  }

  private endMatch(): void {
    this.phase = "match-end";
    this.active = null;
    this.deadline = null;
    this.activePlayerId = null;
    const { rose, slate } = this.teams;
    this.winner =
      rose.score === slate.score
        ? "tie"
        : rose.score > slate.score
          ? "rose"
          : "slate";
  }

  private other(team: TeamId): TeamId {
    return team === "rose" ? "slate" : "rose";
  }

  private pointsForRank(rank: number): number {
    const table = this.rules.rankPoints;
    return table[rank - 1] ?? table[table.length - 1] ?? 0;
  }

  /**
   * Drive time-based transitions. Safe to call at any frequency; it only acts
   * when a deadline has actually passed.
   */
  tick(now: number): boolean {
    if (this.deadline === null || now < this.deadline) return false;
    switch (this.phase) {
      case "category-ban":
        // A team that never used its ban simply forfeits it — the match must
        // not stall because nobody tapped.
        this.resolveBansAndDeal(now);
        return true;
      case "category-intro":
        this.beginPlay(now);
        return true;
      case "playing":
        this.resolveTimeout(now);
        return true;
      case "category-reveal":
        // Straight on to the next category. The reveal and the scoreboard used
        // to be separate phases with a clock each, which read as the game
        // stalling twice in a row.
        this.nextCategory(now);
        return true;
      default:
        return false;
    }
  }

  /** Skip whatever non-playing wait is on screen. */
  advance(now: number): boolean {
    if (
      this.phase === "playing" ||
      this.phase === "lobby" ||
      this.phase === "match-end"
    ) {
      return false;
    }
    this.deadline = now;
    return this.tick(now);
  }

  // --------------------------------------------------------------- guesses

  private targets(): MatchTarget[] {
    if (!this.active) return [];
    const answers: MatchTarget[] = this.active.slots.map((s) => ({
      rank: s.rank,
      text: s.answer.text,
      aliases: s.answer.aliases,
      revealed: s.revealed,
      kind: "answer",
    }));
    const near: MatchTarget[] = (this.active.bank.nearMisses ?? []).map(
      (n) => ({
        rank: n.rank,
        text: n.text,
        aliases: n.aliases,
        revealed: false,
        kind: "near-miss",
      }),
    );
    return [...answers, ...near];
  }

  /** Run the matcher without mutating anything — used by the dev sandbox. */
  previewGuess(text: string): MatchOutcome {
    return matchGuess(text, this.targets(), this.matcherCfg);
  }

  submitGuess(
    playerId: string,
    text: string,
    now: number,
  ): { result: GuessResult; outcome: MatchOutcome } | { error: string } {
    if (this.phase !== "playing" || !this.active)
      return { error: "Not accepting answers" };
    if (playerId !== this.activePlayerId) return { error: "Not your turn" };
    const player = this.players.find((p) => p.id === playerId);
    if (!player) return { error: "Unknown player" };
    if (text.trim().length === 0) return { error: "Type something first" };

    const outcome = matchGuess(text, this.targets(), this.matcherCfg);
    this.lastClosestRank = outcome.closest?.rank ?? null;

    const result: GuessResult = {
      seq: ++this.seq,
      playerId,
      playerName: player.name,
      teamId: player.teamId,
      raw: text.trim(),
      verdict: outcome.verdict,
      // outcome.reason is deliberately not copied here — see GuessResult.
      ...(outcome.ambiguous ? { ambiguous: true } : {}),
    };

    switch (outcome.verdict) {
      case "correct": {
        const slot = this.active.slots.find((s) => s.rank === outcome.rank)!;
        slot.revealed = true;
        slot.foundBy = {
          teamId: player.teamId,
          playerId,
          playerName: player.name,
        };
        this.teams[player.teamId].score += slot.points;
        player.correctCount++;
        result.rank = slot.rank;
        result.points = slot.points;
        // Any correct answer wipes the slate: the two strikes that close a
        // category have to be consecutive.
        this.strikes = 0;
        this.lastResult = result;

        if (this.active.slots.every((s) => s.revealed)) {
          this.teams[player.teamId].score += this.rules.clearBoardBonus;
          result.points = slot.points + this.rules.clearBoardBonus;
          this.closeCategory("cleared", now);
        } else {
          // Control stays with this team; the next player in their rotation is up.
          this.activePlayerId = this.nextPlayerId(this.activeTeam);
          this.deadline = now + this.rules.answerSeconds * 1000;
        }
        return { result, outcome };
      }

      case "duplicate": {
        // Already on the board. Not a wrong answer, so no strike and no handover
        // — but the clock keeps running, which is the real cost.
        result.rank = outcome.rank;
        this.lastResult = result;
        if (this.rules.duplicateCostsStrike) this.registerStrike(now);
        return { result, outcome };
      }

      case "near-miss": {
        const near = this.active.bank.nearMisses?.find(
          (n) => n.rank === outcome.rank,
        );
        if (near) result.nearMiss = near;
        this.lastResult = result;
        this.registerStrike(now);
        return { result, outcome };
      }

      case "wrong": {
        this.lastResult = result;
        this.registerStrike(now);
        return { result, outcome };
      }
    }
  }

  /**
   * "I don't know" — hand over immediately instead of stalling until the clock
   * expires.
   *
   * Costs exactly what a wrong answer costs: a strike, and control to the
   * other team. Anything cheaper and the rotation collapses, because a team
   * with a weak player up simply passes until their strongest comes round —
   * which is the same stall the pass exists to prevent. It buys the room
   * tempo, nothing else. Clear `passCostsStrike` to make it free instead, and
   * the turn moves to the next player on the same side with a fresh clock.
   */
  passTurn(
    playerId: string,
    now: number,
  ): { result: GuessResult } | { error: string } {
    if (this.phase !== "playing" || !this.active)
      return { error: "Not accepting answers" };
    if (playerId !== this.activePlayerId) return { error: "Not your turn" };
    const player = this.players.find((p) => p.id === playerId);
    if (!player) return { error: "Unknown player" };

    const result: GuessResult = {
      seq: ++this.seq,
      playerId,
      playerName: player.name,
      teamId: player.teamId,
      raw: "",
      verdict: "pass",
    };
    this.lastResult = result;
    this.lastClosestRank = null;
    if (this.rules.passCostsStrike) this.registerStrike(now);
    else {
      this.activePlayerId = this.nextPlayerId(this.activeTeam);
      this.deadline = now + this.rules.answerSeconds * 1000;
    }
    return { result };
  }

  private resolveTimeout(now: number): void {
    const player = this.players.find((p) => p.id === this.activePlayerId);
    this.lastResult = {
      seq: ++this.seq,
      playerId: this.activePlayerId ?? "",
      playerName: player?.name ?? "",
      teamId: this.activeTeam,
      raw: "",
      verdict: "timeout",
    };
    this.lastClosestRank = null;
    if (this.rules.timeoutCostsStrike) this.registerStrike(now);
    else {
      this.activePlayerId = this.nextPlayerId(this.activeTeam);
      this.deadline = now + this.rules.answerSeconds * 1000;
    }
  }

  /** A wrong answer: bank the strike, then either close out or hand over. */
  private registerStrike(now: number): void {
    this.strikes++;
    if (this.strikes >= this.rules.strikeLimit) {
      this.closeCategory("strikes", now);
      return;
    }
    this.activeTeam = this.other(this.activeTeam);
    this.activePlayerId = this.nextPlayerId(this.activeTeam);
    if (this.activePlayerId === null) {
      // Other side is empty — bounce back rather than deadlock.
      this.activeTeam = this.other(this.activeTeam);
      this.activePlayerId = this.nextPlayerId(this.activeTeam);
      if (this.activePlayerId === null)
        return this.closeCategory("strikes", now);
    }
    this.deadline = now + this.rules.answerSeconds * 1000;
  }

  closeCategory(reason: CloseReason, now: number): void {
    if (!this.active) return;
    const remaining = this.active.slots.filter((s) => !s.revealed).length;
    for (const slot of this.active.slots) slot.revealed = true;
    this.phase = "category-reveal";
    this.activePlayerId = null;
    this.closeReason = reason;
    // One clock covering the whole beat: long enough for the stagger to finish
    // filling the board, then time to read the scores before the next category.
    this.deadline =
      now +
      remaining * this.rules.revealStaggerMs +
      this.rules.intermissionSeconds * 1000;
  }

  closeReason: CloseReason | null = null;

  /**
   * TV safety net for the one time the matcher gets it wrong. Accepting credits
   * the closest board entry to the player who just guessed; rejecting takes back
   * the last correct answer and applies the strike that should have happened.
   */
  override(accept: boolean, now: number): boolean {
    const last = this.lastResult;
    if (!last || !this.active) return false;

    if (accept) {
      if (last.verdict === "correct" || last.verdict === "duplicate")
        return false;
      const rank = this.lastClosestRank;
      if (rank === null) return false;
      const slot = this.active.slots.find((s) => s.rank === rank);
      if (!slot || slot.revealed) return false;

      // Undo the strike and handover that the rejection caused.
      this.strikes = Math.max(0, this.strikes - 1);
      if (this.phase === "category-reveal") {
        for (const s of this.active.slots) {
          if (!s.foundBy) s.revealed = false;
        }
        this.phase = "playing";
        this.closeReason = null;
      }
      slot.revealed = true;
      slot.foundBy = {
        teamId: last.teamId,
        playerId: last.playerId,
        playerName: last.playerName,
      };
      this.teams[last.teamId].score += slot.points;
      const player = this.players.find((p) => p.id === last.playerId);
      if (player) player.correctCount++;

      this.activeTeam = last.teamId;
      this.activePlayerId = this.nextPlayerId(this.activeTeam);
      this.deadline = now + this.rules.answerSeconds * 1000;
      this.lastResult = {
        ...last,
        verdict: "correct",
        rank: slot.rank,
        points: slot.points,
      };
      return true;
    }

    if (last.verdict !== "correct" || last.rank === undefined) return false;
    const slot = this.active.slots.find((s) => s.rank === last.rank);
    if (!slot || !slot.revealed) return false;
    slot.revealed = false;
    slot.foundBy = undefined;
    this.teams[last.teamId].score -= last.points ?? 0;
    const player = this.players.find((p) => p.id === last.playerId);
    if (player) player.correctCount = Math.max(0, player.correctCount - 1);
    this.lastResult = {
      ...last,
      verdict: "wrong",
      rank: undefined,
      points: undefined,
    };
    this.registerStrike(now);
    return true;
  }

  // ------------------------------------------------------------------ view

  /**
   * The redacted state clients receive. Unrevealed answers never appear here —
   * a phone with dev tools open can read every byte it is sent and still not
   * see the board.
   */
  view(): RoomView {
    const category: CategoryView | null = this.active
      ? {
          id: this.active.bank.id,
          prompt: this.active.bank.prompt,
          subtitle: this.active.bank.subtitle,
          topic: this.active.bank.topic,
          difficulty: this.active.bank.difficulty,
          index: this.active.index,
          slots: this.active.slots.map<BoardSlot>((s) =>
            s.revealed
              ? {
                  rank: s.rank,
                  points: s.points,
                  revealed: true,
                  text: s.answer.text,
                  value: s.answer.value,
                  note: s.answer.note,
                  foundBy: s.foundBy,
                }
              : { rank: s.rank, points: s.points, revealed: false },
          ),
          source:
            this.phase === "category-reveal"
              ? this.active.bank.source
              : undefined,
        }
      : null;

    return {
      code: this.code,
      phase: this.phase,
      teams: { rose: { ...this.teams.rose }, slate: { ...this.teams.slate } },
      players: this.players.map((p) => ({ ...p })),
      category,
      // The planned length: rounds are dealt as they open, so the dealt list
      // alone would under-report the match until its final round.
      categoryCount: this.totalPlanned(),
      // beginRound counts one past the end to discover the match is over, so
      // clamp: nobody should ever read "round 6 of 5" off the final screen.
      round: Math.min(this.round, this.rules.roundsPerMatch),
      roundCount: this.rules.roundsPerMatch,
      activeTeam: this.activeTeam,
      activePlayerId: this.activePlayerId,
      strikes: this.strikes,
      strikeLimit: this.rules.strikeLimit,
      deadline: this.deadline,
      lastResult: this.lastResult,
      offers: this.offerViews(),
      bansRemaining: {
        rose: Math.max(0, this.rules.bansPerTeam - this.bans.rose.length),
        slate: Math.max(0, this.rules.bansPerTeam - this.bans.slate.length),
      },
      winner: this.winner,
    };
  }
}

export { TEAM_IDS };
